// src/pages/project/settings/ProjectSettingsLayout.tsx
import React, { useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { useProjectAccessCtx } from '../../../features/projects/ProjectAccessContext';
import { hasPerm } from '../../../features/projects/hooks/useProjectAccess';

const tabBase = 'px-3 py-1.5 text-xs rounded-full border transition-colors';
const tabActive = 'bg-sky-600 text-white border-sky-600';
const tabInactive = 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50';

const ProjectSettingsLayout: React.FC = () => {
    const { projectId } = useParams();
    const nav = useNavigate();
    const { access, isLoading: accessLoading } = useProjectAccessCtx();

    useEffect(() => {
        if (!projectId) return;
        if (accessLoading) return;
        const ok = hasPerm(access?.permissions, 'project.settings.open');
        if (!ok) nav(`/projects/${projectId}`, { replace: true });
    }, [projectId, accessLoading, access?.permissions, nav]);

    if (accessLoading) return <div className="p-4 text-xs text-gray-500">권한 확인 중…</div>;
    if (!hasPerm(access?.permissions, 'project.settings.open')) return null;

    return (
        <div className="p-4 space-y-4">
            <div>
                <h2 className="text-sm font-semibold text-gray-800">Project Settings</h2>
                <p className="text-xs text-gray-500 mt-1">이 프로젝트의 기본 정보, 멤버, WBS 그룹을 관리합니다.</p>
            </div>

            <div className="flex items-center gap-2 text-xs">
                <NavLink
                    to={`/projects/${projectId}/settings/general`}
                    className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabInactive}`}
                    end
                >
                    General
                </NavLink>
                <NavLink
                    to={`/projects/${projectId}/settings/members`}
                    className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabInactive}`}
                >
                    프로젝트 멤버 관리
                </NavLink>
                <NavLink
                    to={`/projects/${projectId}/settings/orgs`}
                    className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabInactive}`}
                >
                    내부 조직 관리
                </NavLink>
                <NavLink
                    to={`/projects/${projectId}/settings/companies`}
                    className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabInactive}`}
                >
                    외부 파트너 관리
                </NavLink>
                <NavLink
                    to={`/projects/${projectId}/settings/wbs-groups`}
                    className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabInactive}`}
                >
                    WBS Groups
                </NavLink>
                <NavLink
                    to={`/projects/${projectId}/settings/fx`}
                    className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabInactive}`}
                >
                    FX
                </NavLink>
            </div>

            <div className="rounded-2xl border bg-white shadow-sm">
                <Outlet />
            </div>
        </div>
    );
};

export default ProjectSettingsLayout;
